import { createContext } from 'react';
import { PreferenceService } from '@bingads-webui/preference-service';
import { isProdEnv } from '@bingads-webui-react/copilot-utils';
import { ENABLE_NAV_REFRESH_NEW_KEY } from './constants';
import type { PreferenceUtilityPermissions } from './types';

export interface IUXRefreshToggleContextValue {
  isUXRefreshEnabled: boolean;
  isUXRefreshPilot: boolean;
  setUXRefreshEnabled: (enabled: boolean) => void;
}

const defaultContextValue: IUXRefreshToggleContextValue = {
  isUXRefreshEnabled: false,
  isUXRefreshPilot: false,
  setUXRefreshEnabled: () => {},
};

export const UXRefreshToggleContext = createContext<IUXRefreshToggleContextValue>(defaultContextValue);

UXRefreshToggleContext.displayName = 'UXRefreshToggleContext';

// Internal-user auto-enablement must NOT apply in automation/test environments
// (SI / Beta SI / CI / onebox), otherwise every internal Selenium test user is
// flipped into the UX-refresh layout and legacy-layout assertions break. Gate
// the IsInternalUser clause behind a real (prod/betaprod/tip) host check.
export const hasUXRefreshPermission = (
  permissions?: PreferenceUtilityPermissions,
  isUXRefreshTreatment = false
): boolean =>
  !!((permissions?.dynamic?.NavigationRefreshNew ||
    (isProdEnv() && permissions?.IsInternalUser) ||
    isUXRefreshTreatment) &&
    permissions?.dynamic?.NavigationRefresh);

// @aiContributed-MAPFuse-2026-05-28
// Single source of truth for the UX-refresh routing toggle. All runtime
// consumers (withGridProvider, isFluentGridPreFetchEnabledForPath, etc.)
// must call isUXRefreshEnabled() rather than reading the preference
// directly, so renaming ENABLE_NAV_REFRESH_NEW_KEY stays consistent across
// routing, prefetch, and grid-context wiring.
export const getUXRefreshEnabledFromPreference = (
  permissions?: PreferenceUtilityPermissions,
  isUXRefreshTreatment = false
): boolean => {
  if (!hasUXRefreshPermission(permissions, isUXRefreshTreatment)) {
    return false;
  }

  const preference = PreferenceService.findByNameAtUserLevel(ENABLE_NAV_REFRESH_NEW_KEY);
  // Default ON for pilot users when no explicit preference has been saved yet.
  if (!preference || preference.Data === undefined || preference.Data === null) {
    return true;
  }

  return preference.Data !== false;
};

export const isUXRefreshEnabled = (
  permissions?: PreferenceUtilityPermissions,
  isUXRefreshTreatment = false
): boolean =>
  getUXRefreshEnabledFromPreference(permissions, isUXRefreshTreatment);

// Pilot membership only — reflects the UX-refresh permissions WITHOUT considering
// the user's saved preference. Use this to gate things that key off whether the
// user is in the pilot (e.g. showing the opt-in toggle, onboarding dialog, or
// pilot-membership telemetry), as opposed to the effective toggle state.
export const getIsUXRefreshPilot = (
  permissions?: PreferenceUtilityPermissions,
  isUXRefreshTreatment = false
): boolean =>
  hasUXRefreshPermission(permissions, isUXRefreshTreatment);
